import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Snackbar
} from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory, Link as RouterLink } from 'react-router-dom';

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

const useStyles = makeStyles((theme) => ({
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: theme.spacing(2)
  },
  paper: {
    padding: theme.spacing(4),
    maxWidth: 600,
    width: '100%',
    textAlign: 'center'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2)
  },
  submitButton: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    '&:hover': {
      background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
    }
  },
  title: {
    marginBottom: theme.spacing(3),
    color: theme.palette.primary.main,
    fontWeight: 'bold'
  },
  link: {
    marginTop: theme.spacing(2),
    textAlign: 'center'
  },
  benefits: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    width: '100%'
  }
}));

function AffiliateRegister() {
  const classes = useStyles();
  const history = useHistory();
  const [formData, setFormData] = useState({
    personalInfo: {
      fullName: '',
      email: '',
      phone: '',
      idNumber: '',
      address: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'Ghana'
      },
      dateOfBirth: '',
      gender: '',
      nationality: 'Ghanaian',
      emergencyContact: {
        name: '',
        relationship: '',
        phone: ''
      }
    },
    mobileMoneyDetails: {
      provider: '',
      mobileNumber: '',
      accountName: ''
    },
    password: '',
    confirmPassword: '',
    notes: '',
    acceptTerms: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [country, setCountry] = useState('');

  const handleChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    
    if (!name) return;
    
    if (name.includes('.')) {
      const [parent, child, subChild] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: subChild ? {
            ...prev[parent][child],
            [subChild]: value
          } : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const validateForm = () => {
    if (!formData.personalInfo.fullName || !formData.personalInfo.email || 
        !formData.personalInfo.phone || !formData.password || !formData.confirmPassword) {
      const errorMsg = 'Please fill in all required fields';
      setError(errorMsg);
      setNotification({
        open: true,
        message: errorMsg,
        severity: 'error'
      });
      return false;
    }
    
    if (formData.password !== formData.confirmPassword) {
      const errorMsg = 'Passwords do not match';
      setError(errorMsg);
      setNotification({
        open: true,
        message: errorMsg,
        severity: 'error'
      });
      return false;
    }
    
    if (formData.password.length < 6) {
      const errorMsg = 'Password must be at least 6 characters long';
      setError(errorMsg);
      setNotification({
        open: true,
        message: errorMsg,
        severity: 'error'
      });
      return false;
    }

    if (!formData.mobileMoneyDetails.provider || !formData.mobileMoneyDetails.mobileNumber || !formData.mobileMoneyDetails.accountName) {
      const errorMsg = 'Please fill in all mobile money details';
      setError(errorMsg);
      setNotification({
        open: true,
        message: errorMsg,
        severity: 'error'
      });
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    try {
      // Ensure phone number is in E.164 format (starts with +)
      let phoneNumber = formData.personalInfo.phone.trim();
      if (!phoneNumber.startsWith('+')) {
        // If it starts with 0, replace with +233 (Ghana country code)
        if (phoneNumber.startsWith('0')) {
          phoneNumber = '+233' + phoneNumber.substring(1);
        } else if (phoneNumber.startsWith('233')) {
          phoneNumber = '+' + phoneNumber;
        } else {
          // Assume Ghana number and add +233
          phoneNumber = '+233' + phoneNumber.replace(/^\+?233/, '');
        }
      }

      // Map mobileMoneyDetails to bankDetails for backend compatibility
      const registrationData = {
        personalInfo: {
          ...formData.personalInfo,
          phone: phoneNumber
        },
        bankDetails: {
          bankName: formData.mobileMoneyDetails.provider, // Map provider to bankName
          accountNumber: formData.mobileMoneyDetails.mobileNumber, // Map mobileNumber to accountNumber
          accountName: formData.mobileMoneyDetails.accountName,
          accountType: 'savings' // Default for mobile money
        },
        password: formData.password,
        notes: formData.notes
      };

      // Send country if manually selected, otherwise backend will auto-detect from phone
      if (country) {
        registrationData.country = country;
      }

      // Use full backend URL or relative if proxied
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://servermain.sisrevolution.com';
      const response = await fetch(`${API_BASE_URL}/api/affiliates/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(registrationData)
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Registration successful! You will receive an email once your account is approved.');
        // Show notification
        setNotification({
          open: true,
          message: 'Registration successful! You will receive an email once your account is approved.',
          severity: 'success'
        });
        setTimeout(() => {
          history.push('/affiliate/login');
        }, 3000);
      } else {
        // Show validation errors if available
        const errorMessage = data.message || 
          (data.errors && data.errors.length > 0 ? data.errors[0].msg : null) ||
          'Registration failed. Please try again.';
        setError(errorMessage);
        // Show error notification
        setNotification({
          open: true,
          message: errorMessage,
          severity: 'error'
        });
      }
    } catch (err) {
      console.error('Registration error:', err);
      const errorMsg = 'Network error. Please check your connection and try again.';
      setError(errorMsg);
      // Show error notification
      setNotification({
        open: true,
        message: errorMsg,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className={classes.container}>
      <Paper className={classes.paper} elevation={3}>
        <Typography variant="h4" className={classes.title}>
          Join Our Affiliate Program
        </Typography>
        
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Start earning 100 GHS for every successful school referral that uses the system for a term
        </Typography>

        {error && (
          <Paper style={{ padding: 16, backgroundColor: '#ffebee', color: '#c62828', marginBottom: 16 }}>
            <Typography variant="body1">{error}</Typography>
          </Paper>
        )}

        {success && (
          <Paper style={{ padding: 16, backgroundColor: '#e8f5e9', color: '#2e7d32', marginBottom: 16 }}>
            <Typography variant="body1">{success}</Typography>
          </Paper>
        )}

        <form onSubmit={handleSubmit} className={classes.form}>
          {/* Personal Information */}
          <Typography variant="h6" style={{ marginTop: 16, marginBottom: 8, textAlign: 'left' }}>
            Personal Information
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                name="personalInfo.fullName"
                label="Full Name *"
                value={formData.personalInfo.fullName}
                onChange={handleChange}
                required
                fullWidth
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="personalInfo.email"
                label="Email Address *"
                type="email"
                value={formData.personalInfo.email}
                onChange={handleChange}
                required
                fullWidth
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="personalInfo.phone"
                label="Phone Number *"
                value={formData.personalInfo.phone}
                onChange={handleChange}
                required
                fullWidth
                variant="outlined"
                placeholder="+233XXXXXXXXX"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Country (Optional - Auto-detected from phone)</InputLabel>
                <Select
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  label="Country (Optional - Auto-detected from phone)"
                >
                  <MenuItem value="">
                    <em>Auto-detect from phone number</em>
                  </MenuItem>
                  <MenuItem value="GH">Ghana</MenuItem>
                  <MenuItem value="LR">Liberia</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="personalInfo.idNumber"
                label="ID Number"
                value={formData.personalInfo.idNumber}
                onChange={handleChange}
                fullWidth
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="personalInfo.address.street"
                label="Street Address"
                value={formData.personalInfo.address.street}
                onChange={handleChange}
                fullWidth
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="personalInfo.address.city"
                label="City"
                value={formData.personalInfo.address.city}
                onChange={handleChange}
                fullWidth
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Gender</InputLabel>
                <Select
                  name="personalInfo.gender"
                  value={formData.personalInfo.gender}
                  onChange={handleChange}
                  label="Gender"
                >
                  <MenuItem value="male">Male</MenuItem>
                  <MenuItem value="female">Female</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="personalInfo.dateOfBirth"
                label="Date of Birth"
                type="date"
                value={formData.personalInfo.dateOfBirth}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          {/* Mobile Money Details */}
          <Typography variant="h6" style={{ marginTop: 16, marginBottom: 8, textAlign: 'left' }}>
            Mobile Money Details
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined" required>
                <InputLabel>Mobile Money Provider *</InputLabel>
                <Select
                  name="mobileMoneyDetails.provider"
                  value={formData.mobileMoneyDetails.provider}
                  onChange={handleChange}
                  label="Mobile Money Provider *"
                >
                  <MenuItem value="MTN">MTN Mobile Money</MenuItem>
                  <MenuItem value="Vodafone">Vodafone Cash</MenuItem>
                  <MenuItem value="AirtelTigo">AirtelTigo Money</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="mobileMoneyDetails.mobileNumber"
                label="Mobile Money Number *"
                value={formData.mobileMoneyDetails.mobileNumber}
                onChange={handleChange}
                required
                fullWidth
                variant="outlined"
                placeholder="0244XXXXXX or 050XXXXXXX"
                helperText="Enter your mobile money number (e.g., 0244XXXXXX)"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="mobileMoneyDetails.accountName"
                label="Account Name (Name on Mobile Money Account) *"
                value={formData.mobileMoneyDetails.accountName}
                onChange={handleChange}
                required
                fullWidth
                variant="outlined"
                helperText="Enter the name registered with your mobile money account"
              />
            </Grid>
          </Grid>

          {/* Password */}
          <Typography variant="h6" style={{ marginTop: 16, marginBottom: 8, textAlign: 'left' }}>
            Account Security
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                name="password"
                label="Password *"
                type="password"
                value={formData.password}
                onChange={handleChange}
                required
                fullWidth
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="confirmPassword"
                label="Confirm Password *"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                fullWidth
                variant="outlined"
              />
            </Grid>
          </Grid>

          <TextField
            name="notes"
            label="Additional Notes (Optional)"
            multiline
            rows={3}
            value={formData.notes}
            onChange={handleChange}
            fullWidth
            variant="outlined"
          />
          
          <Button
            type="submit"
            variant="contained"
            className={classes.submitButton}
            disabled={loading}
            fullWidth
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Register as Affiliate'}
          </Button>
        </form>

        <Box className={classes.link}>
          <Typography variant="body2">
            Already have an account?{' '}
            <RouterLink 
              to="/affiliate/login" 
              style={{ color: '#667eea', textDecoration: 'none', cursor: 'pointer' }}
            >
              Login here
            </RouterLink>
          </Typography>
        </Box>

        <Box className={classes.benefits}>
          <Typography variant="h6" style={{ marginBottom: 8 }}>
            💰 Affiliate Benefits:
          </Typography>
          <Typography variant="body2" style={{ marginBottom: 4 }}>
            • Earn 100 GHS per successful school referral
          </Typography>
          <Typography variant="body2" style={{ marginBottom: 4 }}>
            • Commission paid after school uses the system for a term
          </Typography>
          <Typography variant="body2" style={{ marginBottom: 4 }}>
            • Track all your referrals in real-time
          </Typography>
          <Typography variant="body2">
            • Get paid via mobile money (minimum payout: GHS 700)
          </Typography>
        </Box>
      </Paper>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setNotification({ ...notification, open: false })} 
          severity={notification.severity}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default AffiliateRegister;
